import types from './types';
import FunnelAPI from '../../../api/funnels';

export const actions = {
  async get({ commit, rootGetters }) {
    commit(types.SET_FUNNELS_LOADING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.get(accountId);
      commit(types.SET_FUNNELS, response.data);
      return response.data;
    } catch (error) {
      // Ignore CanceledError - it's expected when navigating away
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return [];
      }
      throw error;
    } finally {
      commit(types.SET_FUNNELS_LOADING, false);
    }
  },

  async show({ commit, rootGetters }, funnelId) {
    commit(types.SET_FUNNELS_LOADING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.show(funnelId);
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } catch (error) {
      // Ignore CanceledError - it's expected when navigating away
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return null;
      }
      throw error;
    } finally {
      commit(types.SET_FUNNELS_LOADING, false);
    }
  },

  async create({ commit, rootGetters }, funnelData) {
    commit(types.SET_FUNNELS_CREATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.create(funnelData);
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_CREATING, false);
    }
  },

  async update({ commit, rootGetters }, { funnelId, ...funnelData }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.update(funnelId, funnelData);
      commit(types.UPDATE_FUNNEL, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  async delete({ commit, rootGetters }, funnelId) {
    commit(types.SET_FUNNELS_DELETING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      await FunnelAPI.delete(funnelId);
      commit(types.DELETE_FUNNEL, funnelId);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_DELETING, false);
    }
  },

  selectFunnel({ commit }, funnelId) {
    commit(types.SET_SELECTED_FUNNEL, funnelId);
  },

  setSelectedFunnel({ commit }, funnelId) {
    commit(types.SET_SELECTED_FUNNEL, funnelId);
  },

  // Templates
  async getTemplates({ commit, rootGetters }) {
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.getTemplates();
      commit(types.SET_FUNNEL_TEMPLATES, response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async createFromTemplate(
    { commit, rootGetters },
    { templateType, customizations }
  ) {
    commit(types.SET_FUNNELS_CREATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.createFromTemplate(
        templateType,
        customizations
      );
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_CREATING, false);
    }
  },

  // Stage Management
  async addStage({ commit, rootGetters }, { funnelId, stageData }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.addStage(funnelId, stageData);
      commit(types.ADD_FUNNEL_STAGE, {
        funnelId,
        stageName: stageData.name,
        stageConfig: response.data,
      });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  async updateStage(
    { commit, rootGetters },
    { funnelId, stageName, stageData }
  ) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.updateStage(
        funnelId,
        stageName,
        stageData
      );
      commit(types.UPDATE_FUNNEL_STAGE, {
        funnelId,
        stageName,
        stageConfig: response.data,
      });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  async removeStage({ commit, rootGetters }, { funnelId, stageName }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      await FunnelAPI.deleteStage(funnelId, stageName);
      commit(types.REMOVE_FUNNEL_STAGE, { funnelId, stageName });
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  async reorderStages({ commit, rootGetters }, { funnelId, stageOrder }) {
    commit(types.SET_FUNNELS_UPDATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.reorderStages(funnelId, stageOrder);
      commit(types.UPDATE_FUNNEL_STAGES, {
        funnelId,
        stages: response.data,
      });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_UPDATING, false);
    }
  },

  // Analytics
  async getAnalytics({ rootGetters }, { funnelId, params }) {
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.getAnalytics(funnelId, params);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getConversionRates({ rootGetters }, { funnelId, params }) {
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.getConversionRates(funnelId, params);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Bulk Operations
  async bulkMoveConversations(
    { rootGetters },
    { conversationIds, targetFunnelId, targetStage }
  ) {
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.bulkMoveConversations(
        conversationIds,
        targetFunnelId,
        targetStage
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Import/Export
  async exportFunnel({ rootGetters }, { funnelId, format }) {
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.exportFunnel(funnelId, format);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async importFunnel({ commit, rootGetters }, { funnelData, options }) {
    commit(types.SET_FUNNELS_CREATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.importFunnel(funnelData, options);
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_CREATING, false);
    }
  },

  // Duplicate
  async duplicate({ commit, rootGetters }, { funnelId, newName }) {
    commit(types.SET_FUNNELS_CREATING, true);
    try {
      const accountId = rootGetters.getCurrentAccountId;
      const response = await FunnelAPI.duplicate(funnelId, newName);
      commit(types.SET_FUNNEL, response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_FUNNELS_CREATING, false);
    }
  },
};
